import type { Condition } from '@atbash-labs/ontology-agent-wire'
import { triggersFromProxyWire, triggersToProxyWire } from '@atbash-labs/ontology-agent-wire'

import type {
  CreateCustomAgentConfigPayload,
  CustomAgentConfig,
  CustomAgentTrigger,
  UpdateCustomAgentConfigPayload
} from './types'

function collectTagPaths(condition: Condition, into: Set<string>): void {
  switch (condition.kind) {
    case 'live':
    case 'agg':
    case 'deviation':
    case 'alarm_active':
      if (condition.tag_path) into.add(condition.tag_path)
      return
    case 'expr':
      for (const src of Object.values(condition.sources)) {
        if (src.tag_path) into.add(src.tag_path)
      }
      return
    case 'and':
    case 'or':
      for (const operand of condition.operands) collectTagPaths(operand, into)
      return
    case 'not':
      collectTagPaths(condition.operand, into)
  }
}

export function deriveCustomAgentTagPaths(triggers: CustomAgentTrigger[]): string[] {
  const set = new Set<string>()
  for (const trigger of triggers) collectTagPaths(trigger.condition, set)
  return Array.from(set).sort()
}

export function normalizeCustomAgentConfig(config: CustomAgentConfig): CustomAgentConfig {
  const triggers = triggersFromProxyWire(config.triggers ?? [])
  return {
    ...config,
    triggers,
    tag_paths: deriveCustomAgentTagPaths(triggers),
    email_addresses: config.email_addresses ?? []
  }
}

export function normalizeCustomAgentConfigList(configs: CustomAgentConfig[]): CustomAgentConfig[] {
  return configs.map(normalizeCustomAgentConfig)
}

/**
 * Outbound twin of {@link normalizeCustomAgentConfig}: the form `Condition`
 * (`kind` / `operands` / `operand`, read-only `tag_path` echoes) becomes the
 * proxy wire shape (`op` / `children` / `child`, `tag_id` only). Same contract
 * `notification-rule-transform.ts` applies to rule bodies.
 */
export function denormalizeCreateCustomAgentConfigPayload(
  body: CreateCustomAgentConfigPayload
): CreateCustomAgentConfigPayload {
  if (!body.triggers) return body
  return { ...body, triggers: triggersToProxyWire(body.triggers) }
}

/** A patch without `triggers` (state / labels / notifications) is returned as-is. */
export function denormalizeUpdateCustomAgentConfigPayload(
  patch: UpdateCustomAgentConfigPayload
): UpdateCustomAgentConfigPayload {
  if (!patch.triggers) return patch
  return { ...patch, triggers: triggersToProxyWire(patch.triggers) }
}
